import {
  MarketingSpendDeprecated,
  ScenarioActivityStatus,
  ScenarioBase,
  ScenarioStatus,
  SpendConstraint,
} from "@mutiny-group/sbo-openapi-client";
import { useQuery } from "@tanstack/react-query";

import { QueryConfig } from "@/lib/react-query";
import { ScenarioBuilderClient } from "@/pages/scenario-builder/api/client/scenarioClient";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import { Status } from "@/pages/scenario-builder/types";
import dayjs from "dayjs";

const getScenarioV2 = (scenarioBuilderClient: ScenarioBuilderClient, uuid: string) => {
  return scenarioBuilderClient.scenarios.getScenario({
    scenarioUuid: uuid,
  });
};

type UseScenarioV2Options = {
  uuid: string;
  config?: QueryConfig<any>;
};

export const useScenarioV2 = ({ uuid, config }: UseScenarioV2Options) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  return useQuery({
    ...config,
    queryKey: ["scenario-v2", uuid, scenarioBuilderClient],
    queryFn: async () => await getScenarioV2(scenarioBuilderClient, uuid),
    select: (data) => {
      return {
        ...data,
        brand: data.request.scope?.brand ?? "default",
        scenarioStatus: getScenarioStatus(data),
        scenarioType: data.scenarioDisplayType,
        value: data.totalBudget,
        startDate: data.request.startDate,
        endDate: data.request.endDate,
        geography: data.request.scope?.geographies?.[0] ?? null,
        isEligibleForActionPlans: isScenarioEligibleForActionPlans(data.createdAt),
        scenarioConstraints: (function () {
          if (!data.optimizerSpendConstraints && !data.forecasterMarketingSpends) return [];
          if (!data.scenarioDisplayType) return [];

          if (data.scenarioDisplayType === "optimizer") {
            return data.optimizerSpendConstraints?.map((constraint: SpendConstraint) => ({
              minValue: constraint.minSpend,
              maxValue: constraint.maxSpend,
              channel: constraint.channel,
              product: constraint.marketingSpendNode.product,
              category: constraint.marketingSpendNode.category,
              brand: constraint.marketingSpendNode.brand,
              geography: constraint.geography,
            }));
          }

          if (data.scenarioDisplayType === "budget") {
            return data.forecasterMarketingSpends?.map((spend: MarketingSpendDeprecated) => ({
              minValue: 0,
              maxValue: spend.spend,
              channel: spend.channel,
              product: spend.marketingSpendNode?.product,
              category: spend.marketingSpendNode?.category,
              brand: spend.marketingSpendNode?.brand,
              geography: spend.geography,
            }));
          }

          return [];
        })(),
      };
    },
  });
};

export const getScenarioStatus = (scenario: ScenarioBase) => {
  if (
    scenario.status === ScenarioStatus.PredictionFailed ||
    scenario.status === ScenarioStatus.Failed
  ) {
    return Status.fromString("error");
  }

  if (scenario.activityStatus === ScenarioActivityStatus.Submitted) {
    return Status.fromString("processing");
  }

  if (scenario.activityStatus === ScenarioActivityStatus.ReadyForReview) {
    return Status.fromString("completed");
  }

  if (scenario.activityStatus === ScenarioActivityStatus.Cloned) {
    return Status.fromString("draft");
  }

  return Status.fromString(scenario.activityStatus || "");
};

// Action plans are only available for scenarios created after this date
const ACTION_PLAN_ELIGIBILITY_CUTOFF_DATE = dayjs("2025-10-28"); // October 28, 2025

export const isScenarioEligibleForActionPlans = (createdAt: Date | string | undefined) => {
  if (!createdAt) return false;
  let date = createdAt;
  if (typeof createdAt === "string") {
    date = new Date(createdAt);
  }
  return dayjs(date).isAfter(ACTION_PLAN_ELIGIBILITY_CUTOFF_DATE);
};
